/**
 * LEGAL-6: Tenant-admin surface for operator obligations & bindings — a
 * role-scoped projection of the LEGAL-4 admin UI, NOT a second UI.
 *
 * Defining property: READ law, WRITE bindings.
 *  - The legal layer (obligations, provenance, coverage) is strictly
 *    read-only here — legal correctness carries the provider's documented
 *    diligence, never tenant adjustment.
 *  - Bindings (acceptance, suspension, FM delegation) are the only writable
 *    objects; scope/overlap/delegation resolution is server-side.
 *
 * Every binding creation goes through the effect preview ("matches N assets,
 * creates M checklists now, ...") — acceptance of a duty without visible
 * consequence is not allowed.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, BarChart3, CircleX, Info, ListX, Loader2, Pause, Play, Plus, RefreshCw, UserCog } from "lucide-react";

import type { ColumnDef } from "@tanstack/react-table";

import { useModalContext } from "../../components/ModalProvider";
import ConfirmationDialog from "../../general/ConfirmationDialog";
import { authenticationAtom } from "../../State";
import { useRoleAccess } from "../../hooks/useRoleAccess";
import { useLegalBindingAPI, BindingScope } from "../../LegalBindingAPI";
import CostParametrizationPanel from "./CostParametrizationPanel";
import CostBreakdownPanel from "./CostBreakdownPanel";
import SubscriptionDashboardPanel from "./SubscriptionDashboardPanel";
import SubscriptionExclusionsPanel from "./SubscriptionExclusionsPanel";
import MaintenanceIntervalPanel from "./MaintenanceIntervalPanel";
import DataModelPanel from "./data-model/DataModelPanel";
import LicenseAttestationPanel from "./LicenseAttestationPanel";
import LegalChangesPanel, { CHANGE_PARAM } from "./LegalChangesPanel";
import {
  ReviewStatusBadge,
  ProvenanceBadge,
  CoverageStatusBadge,
  regulatedObjectName,
  formatDate,
} from "../../administration/operator-responsibility/legalDisplay";
import { bindingScopesEqual } from "./bindingScopeComparison";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {}

/**
 * Die Reiter in Reihenfolge — zugleich die Werte von `?tab=`. Die Schlüssel sind die bisherigen
 * Reiterwerte, damit Registry-Einträge (`settings.operatorObligations.tab.bindings`) und
 * Tests stabil bleiben.
 */
export const OPERATOR_OBLIGATIONS_TAB_KEYS = [
  "obligations",
  "bindings",
  "coverage",
  "parametrization",
  "cost",
  "intervals",
  "licenses",
  "dataModel",
  "changes",
] as const;
export type OperatorObligationsTab = (typeof OPERATOR_OBLIGATIONS_TAB_KEYS)[number];
const DEFAULT_TAB: OperatorObligationsTab = "obligations";

/** Sprechende Zweitnamen, die eine Adresse auch tragen darf (von Hand geschriebene Links). */
const TAB_ALIASES: Record<string, OperatorObligationsTab> = {
  activations: "bindings",
  costs: "cost",
  maintenance: "intervals",
  rechtsaenderungen: "changes",
};

/** Der Reiter, den `?tab=` meint — unbekannt oder fehlend ergibt den Standardreiter. */
export const operatorObligationsTabFromParam = (value: string | null): OperatorObligationsTab => {
  if (!value) return DEFAULT_TAB;
  if ((OPERATOR_OBLIGATIONS_TAB_KEYS as readonly string[]).includes(value)) return value as OperatorObligationsTab;
  return TAB_ALIASES[value.toLowerCase()] ?? DEFAULT_TAB;
};

const BINDING_STATUS_VARIANTS: Record<string, "success" | "warning" | "secondary"> = {
  active: "success",
  suspended: "warning",
  ended: "secondary",
};

/**
 * Abonniert und aktiviert sind getrennt (14.10.2026): abonniert zeigt Pflichten, Kosten, Ausschlüsse;
 * aktiviert legt Checklisten an, gestoppt friert sie ein. Abonnements von früher tragen kein
 * `activation` — der Server liefert es abgeleitet mit.
 */
const ACTIVATION_VARIANTS: Record<string, "success" | "warning" | "secondary"> = {
  activated: "success",
  stopped: "warning",
  none: "secondary",
};

const ActivationBadge = ({ activation }: { activation: string }) => {
  const { t } = useTranslation();
  return (
    <Badge variant={ACTIVATION_VARIANTS[activation] || "secondary"}>
      {t(`operatorObligationsPanel.activation.${activation || "none"}`)}
    </Badge>
  );
};

const BindingStatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation();

  return (
    <Badge variant={BINDING_STATUS_VARIANTS[status] || "secondary"}>
      {t(`operatorObligationsPanel.bindingStatus.${status}`)}
    </Badge>
  );
};

/** Leerzustand einer Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
      {action}
    </div>
  );
}

export default function ObligationActivationPanel(_properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const { isCustomerAdmin, hasFullAccess } = useRoleAccess();
  const legalAPI = useLegalBindingAPI(authentication?.customer);
  const fieldId = useId();

  const canWrite = isCustomerAdmin || hasFullAccess;

  const [searchParams, setSearchParams] = useSearchParams();
  // Ein Deep-Link auf eine Rechtsänderung (`&change=<guid>`) gehört zum Reiter „Rechtsänderungen“,
  // auch wenn er kein `tab=` trägt.
  const tab: OperatorObligationsTab = searchParams.get(CHANGE_PARAM) && !searchParams.get("tab")
    ? "changes"
    : operatorObligationsTabFromParam(searchParams.get("tab"));
  const setTab = (value: string) => {
    const next = operatorObligationsTabFromParam(value);
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set("tab", next);
        if (next !== "changes") params.delete(CHANGE_PARAM);
        return params;
      },
      { replace: true },
    );
  };
  const [costRefresh, setCostRefresh] = useState(0);
  const [obligations, setObligations] = useState<any[]>([]);
  const [_portfolioCodes, setPortfolioCodes] = useState<string[]>([]);
  const [includeAll, setIncludeAll] = useState(false);
  const [regulatedObjects, setRegulatedObjects] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [bindings, setBindings] = useState<any[]>([]);
  const [coverage, setCoverage] = useState<any>({ gaps: [], coveredCount: 0 });
  const [provenance, setProvenance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [resolvingAssets, setResolvingAssets] = useState(false);

  // binding editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [anchorType, setAnchorType] = useState<"Portfolio" | "Site">("Portfolio");
  const [siteId, setSiteId] = useState("");
  const [allObjects, setAllObjects] = useState(true);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [delegateName, setDelegateName] = useState("");
  const [delegateEmail, setDelegateEmail] = useState("");
  const [preview, setPreview] = useState<any>(null);
  const [conformanceClass, setConformanceClass] = useState("legalMinimum");
  const [includeUnconfirmed, setIncludeUnconfirmed] = useState(false);
  // „Auswirkungen“ of a subscription row: the dashboard of that subscription in a dialog.
  const [impactBinding, setImpactBinding] = useState<any | null>(null);
  const [comparison, setComparison] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [endDialogBinding, setEndDialogBinding] = useState<any>(null);
  const [delegationDialogBinding, setDelegationDialogBinding] = useState<any>(null);
  const [exclusionsBinding, setExclusionsBinding] = useState<any>(null);
  const previewRequestId = useRef(0);

  const load = async () => {
    try {
      const [obligationsResult, objects, sitesResult, bindingsResult, coverageResult] = await Promise.all([
        legalAPI.getObligations(includeAll),
        legalAPI.getRegulatedObjects(),
        legalAPI.getSites(),
        legalAPI.getBindings(),
        legalAPI.getCoverage(),
      ]);

      setObligations(obligationsResult.items || []);
      setPortfolioCodes(obligationsResult.portfolioRegulatedObjects || []);
      setRegulatedObjects(objects || []);
      setSites(sitesResult || []);
      setBindings(bindingsResult || []);
      setCoverage(coverageResult || { gaps: [], coveredCount: 0 });
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
      // Keep the Kosten tab in sync with binding changes even when the
      // obligation/binding reload fails after a successful write.
      setCostRefresh((value) => value + 1);
    }
  };

  // An activation runs in the background (a large site takes minutes): poll the bindings while one runs.
  const activationRunning = bindings.some((binding: any) => binding.activationRun?.status === "running");
  useEffect(() => {
    if (!activationRunning) return;
    const timer = setTimeout(async () => {
      try {
        setBindings((await legalAPI.getBindings()) || []);
      } catch {
        // the next tick tries again
      }
    }, 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationRunning, bindings]);

  useEffect(() => {
    if (authentication?.customer) {
      load();
    } else {
      // Ohne Mandant wird nichts geladen — dann zeigen die Tabellen ihren
      // Leerzustand statt endlos Platzhalter.
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, includeAll]);

  const currentScope = (): BindingScope => ({
    anchor: anchorType === "Site" ? { type: "Site", siteId } : { type: "Portfolio" },
    regulatedObjects: allObjects ? "ALL" : selectedCodes,
  });

  const currentDelegation = () =>
    delegateName.trim() || delegateEmail.trim()
      ? { name: delegateName.trim() || null, email: delegateEmail.trim() || null }
      : null;

  const scopeValid = () =>
    (anchorType === "Portfolio" || siteId) && (allObjects || selectedCodes.length > 0);

  const openEditor = () => {
    setAnchorType("Portfolio");
    setSiteId("");
    setAllObjects(true);
    setSelectedCodes([]);
    setDelegateName("");
    setDelegateEmail("");
    setConformanceClass("legalMinimum");
    setPreview(null);
    setComparison(null);
    setEditorOpen(true);
  };

  const runPreview = async () => {
    const requestId = ++previewRequestId.current;
    setSubmitting(true);

    const requestScope = currentScope();
    const requestClass = conformanceClass;
    const requestDelegation = currentDelegation();

    try {
      // Forward-looking decision support: the effect + cost preview for the
      // chosen class, plus the full conformance-class comparison.
      const [previewResult, comparisonResult] = await Promise.allSettled([
        legalAPI.previewBinding(requestScope, requestDelegation, requestClass, { includeUnconfirmed }),
        legalAPI.costCompare(requestScope, { includeUnconfirmed }),
      ]);
      const scopeMatches = bindingScopesEqual(currentScope(), requestScope);
      const classMatches = conformanceClass === requestClass;
      const delegationMatches = JSON.stringify(currentDelegation()) === JSON.stringify(requestDelegation);
      const isLatestRequest = previewRequestId.current === requestId;

      // Only apply a response that still matches the current inputs (avoid a
      // slow response clobbering a newer one); read .reason only on rejection.
      if (isLatestRequest && scopeMatches && classMatches && delegationMatches) {
        if (previewResult.status === "fulfilled") {
          setPreview(previewResult.value);
        } else {
          setPreview(null);
          pushError(previewResult.reason);
        }
      }
      if (isLatestRequest && scopeMatches) {
        if (comparisonResult.status === "fulfilled") {
          setComparison(comparisonResult.value);
        } else {
          setComparison(null);
          pushError(comparisonResult.reason);
        }
      }
    } catch (error) {
      pushError(error);
    } finally {
      if (previewRequestId.current === requestId) {
        setSubmitting(false);
      }
    }
  };

  const commitBinding = async (activate: boolean) => {
    setSubmitting(true);

    try {
      await legalAPI.createBinding(currentScope(), currentDelegation(), conformanceClass, { activate, includeUnconfirmed });
      setEditorOpen(false);
      await load();
    } catch (error) {
      pushError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatEUR = (value: number, currency = "EUR") =>
    new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value || 0);

  /** The name the subscription is anchored on: the site's name, or „Portfolio“. */
  const anchorName = (binding: any): string =>
    binding?.scope?.anchor?.type === "Site"
      ? sites.find((site: any) => String(site._id) === String(binding.scope.anchor.siteId))?.name ||
        t("operatorObligationsPanel.site")
      : t("operatorObligationsPanel.portfolio");

  const scopeSummary = (binding: any) => {
    const anchor = anchorName(binding);
    const objects =
      binding.scope?.regulatedObjects === "ALL"
        ? t("operatorObligationsPanel.allRegulatedObjects")
        : (binding.scope?.regulatedObjects || [])
            .map((code: string) => regulatedObjectName(t, code))
            .join(", ");

    return `${anchor} × ${objects}`;
  };

  // ------------------------------------------------------------------
  // Columns
  // ------------------------------------------------------------------

  const obligationColumns: ColumnDef<any>[] = [
    {
      id: "obligation",
      accessorFn: (obligation: any) => obligation.obligationKey,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.obligation")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[170px] break-words text-sm">{row.original.obligationKey}</span>
      ),
    },
    {
      id: "description",
      accessorFn: (obligation: any) => obligation.description,
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.description")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[380px] break-words text-sm">{row.original.description || "—"}</span>
      ),
    },
    {
      id: "regulatedObject",
      accessorFn: (obligation: any) => regulatedObjectName(t, obligation.regulatedObjectCode),
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.regulatedObject")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[170px] break-words text-sm">
          {regulatedObjectName(t, row.original.regulatedObjectCode)}
        </span>
      ),
    },
    {
      id: "relevance",
      accessorFn: (obligation: any) => (obligation.relevant ? 0 : 1),
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.relevance")}</span>,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.relevant ? (
          <span className="text-xs font-semibold text-success">
            {t("operatorObligationsPanel.inPortfolio")}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            {t("operatorObligationsPanel.alsoAvailable")}
          </span>
        ),
    },
    {
      id: "status",
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.status")}</span>,
      enableSorting: false,
      cell: ({ row }) => <ReviewStatusBadge status={row.original.reviewStatus} />,
    },
  ];

  const bindingColumns: ColumnDef<any>[] = [
    {
      id: "scope",
      accessorFn: (binding: any) => scopeSummary(binding),
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.scope")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[320px] break-words text-sm">{scopeSummary(row.original)}</span>
      ),
    },
    {
      id: "status",
      accessorFn: (binding: any) => binding.status,
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.status")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-1">
          <BindingStatusBadge status={row.original.status} />
          {row.original.status !== "ended" ? <ActivationBadge activation={row.original.activation} /> : null}
          {row.original.activationRun?.status === "running" ? (
            <Badge variant="outline" className="gap-1">
              <Loader2 aria-hidden className="h-3 w-3 animate-spin" />
              {t("operatorObligationsPanel.activationRunning", {
                done: (row.original.activationRun.done ?? 0).toLocaleString("de-DE"),
                total: (row.original.activationRun.total ?? 0).toLocaleString("de-DE"),
              })}
            </Badge>
          ) : row.original.activationRun?.status === "failed" ? (
            <Badge variant="destructive">{t("operatorObligationsPanel.activationFailed")}</Badge>
          ) : null}
          {row.original.includeUnconfirmed ? (
            <Badge variant="outline">{t("operatorObligationsPanel.includeUnconfirmedBadge")}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: "delegatedTo",
      accessorFn: (binding: any) =>
        binding.delegatedTo ? binding.delegatedTo.name || binding.delegatedTo.email : "—",
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.delegatedTo")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[180px] break-words text-sm">
          {row.original.delegatedTo ? row.original.delegatedTo.name || row.original.delegatedTo.email : "—"}
        </span>
      ),
    },
    {
      id: "matchedAssets",
      accessorFn: (binding: any) => binding.matchedAssets ?? 0,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.matchedAssets")}</span>,
      enableSorting: false,
      cell: ({ row }) => <span className="text-sm tabular-nums">{row.original.matchedAssets ?? 0}</span>,
    },
    {
      id: "openChecklists",
      accessorFn: (binding: any) => binding.openChecklists ?? 0,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.openChecklists")}</span>,
      enableSorting: false,
      cell: ({ row }) => <span className="text-sm tabular-nums">{row.original.openChecklists ?? 0}</span>,
    },
    {
      id: "activatedBy",
      // Subscribed by whom and when; once activated, by whom and when that happened (14.10.2026: a
      // subscribed-only row showed „null · 10/14/2026“).
      accessorFn: (binding: any) => `${binding.subscribedBy ?? binding.activatedBy ?? ""}`,
      header: () => <span className="font-bold text-foreground">{t("operatorObligationsPanel.subscribedBy")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const b = row.original;
        const who = b.subscribedBy ?? b.activatedBy;
        const when = b.subscribedAt ?? b.activatedAt;
        return (
          <div className="flex max-w-[230px] flex-col break-words text-sm">
            <span>{[who, when ? formatDate(when) : null].filter(Boolean).join(" · ") || "—"}</span>
            {b.activation === "activated" && b.activatedBy && (b.activationChangedAt || b.subscribedAt) ? (
              <span className="text-xs text-muted-foreground">
                {t("operatorObligationsPanel.activatedByLine", {
                  who: b.activatedBy,
                  when: formatDate(b.activationChangedAt ?? b.activatedAt),
                })}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">{t("operatorObligationsPanel.exclusions.open")}</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const binding = row.original;
        const running = binding.activationRun?.status === "running";
        // Icon buttons with a tooltip — the label stays for screen readers (aria-label).
        const action = (key: string, label: string, icon: React.ReactNode, onClick: () => void, extra: any = {}) => (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={label} onClick={onClick} {...extra}>
                {icon}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        );
        const run = (fn: () => Promise<any>) => async () => {
          try {
            await fn();
            await load();
          } catch (error) {
            pushError(error);
          }
        };
        if (binding.status === "ended") return null;
        return (
          <TooltipProvider delayDuration={300}>
            <div className="flex flex-nowrap items-center gap-0.5">
              {action("impact", t("operatorObligationsPanel.dashboard.open"), <BarChart3 aria-hidden className="h-4 w-4" />, () => setImpactBinding(binding), {
                "data-testid": "binding-open-dashboard",
              })}
              {action("exclusions", t("operatorObligationsPanel.exclusions.open"), <ListX aria-hidden className="h-4 w-4" />, () => setExclusionsBinding(binding))}
              {canWrite
                ? binding.activation === "activated"
                  ? action("stop", t("operatorObligationsPanel.stop"), <Pause aria-hidden className="h-4 w-4" />, run(() => legalAPI.stopBinding(String(binding._id))), {
                      disabled: running,
                    })
                  : action("activate", t("operatorObligationsPanel.activateBinding"), <Play aria-hidden className="h-4 w-4" />, run(() => legalAPI.activateBinding(String(binding._id))), {
                      disabled: running,
                    })
                : null}
              {canWrite
                ? action("delegate", t("operatorObligationsPanel.delegate"), <UserCog aria-hidden className="h-4 w-4" />, () => {
                    setDelegateName(binding.delegatedTo?.name || "");
                    setDelegateEmail(binding.delegatedTo?.email || "");
                    setDelegationDialogBinding(binding);
                  })
                : null}
              {canWrite ? action("end", t("operatorObligationsPanel.end"), <CircleX aria-hidden className="h-4 w-4" />, () => setEndDialogBinding(binding)) : null}
            </div>
          </TooltipProvider>
        );
      },
    },
  ];

  const coverageColumns: ColumnDef<any>[] = [
    {
      id: "shortName",
      accessorFn: (regulation: any) => regulation.shortName || regulation.eliId,
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.shortName")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[170px] break-words text-sm">
          {row.original.shortName || row.original.eliId}
        </span>
      ),
    },
    {
      id: "title",
      accessorFn: (regulation: any) => regulation.title,
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.regulationTitle")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[280px] break-words text-sm">{row.original.title || "—"}</span>
      ),
    },
    {
      id: "coverage",
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.coverage")}</span>,
      enableSorting: false,
      cell: ({ row }) => <CoverageStatusBadge status={row.original.coverageStatus} />,
    },
    {
      id: "coverageNote",
      accessorFn: (regulation: any) => regulation.coverageNote || "—",
      header: () => <span className="font-bold text-foreground">{t("operatorResponsibilityPanel.coverageNote")}</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block max-w-[420px] break-words text-sm">{row.original.coverageNote || "—"}</span>
      ),
    },
  ];

  const anchorSelectId = `${fieldId}-anchor`;
  const siteSelectId = `${fieldId}-site`;
  const classSelectId = `${fieldId}-class`;
  const delegateNameId = `${fieldId}-delegate-name`;
  const delegateEmailId = `${fieldId}-delegate-email`;
  const delegationNameId = `${fieldId}-delegation-name`;
  const delegationEmailId = `${fieldId}-delegation-email`;
  const includeAllId = `${fieldId}-include-all`;
  const allObjectsId = `${fieldId}-all-objects`;

  const provenanceVersions = provenance?.versions || [];
  const currentProvenanceVersion =
    provenanceVersions.find((version: any) => !version.validTo) || provenanceVersions[provenanceVersions.length - 1];

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-2">
        <h2 className="text-lg font-semibold">{t("operatorObligationsPanel.title")}</h2>
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t("operatorObligationsPanel.subtitle")}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="max-w-full overflow-x-auto">
          <TabsTrigger value="obligations">{t("operatorObligationsPanel.tabs.obligations")}</TabsTrigger>
          <TabsTrigger value="bindings">{t("operatorObligationsPanel.tabs.bindings")}</TabsTrigger>
          <TabsTrigger value="coverage">{t("operatorObligationsPanel.tabs.coverage")}</TabsTrigger>
          <TabsTrigger value="parametrization">{t("operatorObligationsPanel.tabs.parametrization")}</TabsTrigger>
          <TabsTrigger value="cost">{t("operatorObligationsPanel.tabs.cost")}</TabsTrigger>
          <TabsTrigger value="intervals">
            {t("operatorObligationsPanel.tabs.intervals", { defaultValue: "Wartungsintervalle" })}
          </TabsTrigger>
          <TabsTrigger value="licenses">
            {t("operatorObligationsPanel.tabs.licenses", { defaultValue: "Lizenzen" })}
          </TabsTrigger>
          <TabsTrigger value="dataModel">{t("operatorObligationsPanel.tabs.dataModel")}</TabsTrigger>
          <TabsTrigger value="changes">{t("operatorObligationsPanel.tabs.changes")}</TabsTrigger>
        </TabsList>

        <TabsContent value="obligations">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={includeAllId}
                  checked={includeAll}
                  onCheckedChange={(checked) => setIncludeAll(checked === true)}
                />
                <Label htmlFor={includeAllId}>{t("operatorObligationsPanel.showAlsoAvailable")}</Label>
              </div>
              <p className="min-w-0 break-words text-sm text-muted-foreground">
                {t("operatorObligationsPanel.readOnlyNote")}
              </p>
            </div>
            <div className="min-w-0 overflow-x-auto">
              <DataTable
                columns={obligationColumns}
                data={obligations}
                loading={loading}
                pageSize={5}
                onRowClick={async (obligation: any) => {
                  try {
                    setProvenance(await legalAPI.getObligationProvenance(obligation.obligationKey));
                  } catch (error) {
                    pushError(error);
                  }
                }}
                previousAriaLabel={t("global.back")}
                nextAriaLabel={t("global.next")}
                emptyMessage={
                  <TableEmptyState
                    title={t("operatorObligationsPanel.noObligations")}
                    hint={t("operatorObligationsPanel.noObligationsHint")}
                    action={
                      !includeAll ? (
                        <Button variant="outline" size="sm" onClick={() => setIncludeAll(true)}>
                          {t("operatorObligationsPanel.showAlsoAvailable")}
                        </Button>
                      ) : undefined
                    }
                  />
                }
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bindings">
          <div className="flex min-w-0 flex-col gap-4">
            {canWrite ? (
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Button data-guide-id="settings.operatorObligations.createBinding" className="gap-2 max-sm:w-full" onClick={openEditor}>
                  <Plus aria-hidden className="h-4 w-4" />
                  {t("operatorObligationsPanel.createBinding")}
                </Button>
                <Button
                  variant="outline"
                  className="gap-2 max-sm:w-full"
                  disabled={resolvingAssets}
                  onClick={async () => {
                    setResolvingAssets(true);
                    try {
                      const result = await legalAPI.resolveAssets();
                      await load();
                      // eslint-disable-next-line no-console
                      console.log("Asset resolution", result);
                    } catch (error) {
                      pushError(error);
                    } finally {
                      setResolvingAssets(false);
                    }
                  }}
                >
                  {resolvingAssets
                    ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                    : <RefreshCw aria-hidden className="h-4 w-4" />}
                  {t("operatorObligationsPanel.resolveAssets")}
                </Button>
              </div>
            ) : (
              <Alert variant="warning">
                <AlertDescription>{t("operatorObligationsPanel.writeRequiresAdmin")}</AlertDescription>
              </Alert>
            )}
            <div className="min-w-0 overflow-x-auto">
              <DataTable
                columns={bindingColumns}
                data={bindings}
                loading={loading}
                pageSize={5}
                previousAriaLabel={t("global.back")}
                nextAriaLabel={t("global.next")}
                emptyMessage={
                  <TableEmptyState
                    title={t("operatorObligationsPanel.noBindings")}
                    hint={t("operatorObligationsPanel.noBindingsHint")}
                    action={
                      canWrite ? (
                        <Button variant="outline" size="sm" className="gap-2" onClick={openEditor}>
                          <Plus aria-hidden className="h-4 w-4" />
                          {t("operatorObligationsPanel.createBinding")}
                        </Button>
                      ) : undefined
                    }
                  />
                }
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="coverage">
          <div className="flex min-w-0 flex-col gap-4">
            <Alert variant="destructive">
              <AlertTriangle aria-hidden className="h-4 w-4" />
              <AlertTitle>{t("operatorResponsibilityPanel.coverageDisclaimerTitle")}</AlertTitle>
              <AlertDescription>{t("operatorResponsibilityPanel.coverageDisclaimer")}</AlertDescription>
            </Alert>
            <div className="min-w-0 overflow-x-auto">
              <DataTable
                columns={coverageColumns}
                data={coverage.gaps}
                loading={loading}
                previousAriaLabel={t("global.back")}
                nextAriaLabel={t("global.next")}
                emptyMessage={
                  <TableEmptyState
                    title={t("operatorObligationsPanel.noCoverageGaps")}
                    hint={t("operatorObligationsPanel.noCoverageGapsHint")}
                  />
                }
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="parametrization">
          <CostParametrizationPanel canWrite={canWrite} onConfigurationSaved={() => setCostRefresh((value) => value + 1)} />
        </TabsContent>

        <TabsContent value="cost">
          <CostBreakdownPanel sites={sites} refreshSignal={costRefresh} />
        </TabsContent>

        <TabsContent value="intervals">
          <MaintenanceIntervalPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="licenses">
          <LicenseAttestationPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="dataModel">
          <DataModelPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="changes">
          <LegalChangesPanel canWrite={canWrite} regulatedObjects={regulatedObjects} />
        </TabsContent>
      </Tabs>

      {/* Provenance traceback — read-only trust feature */}
      <Dialog open={!!provenance} onOpenChange={(open) => { if (!open) setProvenance(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle className="min-w-0 break-words">{provenance?.obligationKey || ""}</DialogTitle>
            <DialogDescription>{t("operatorObligationsPanel.provenanceSubtitle")}</DialogDescription>
          </DialogHeader>
          {provenance ? (
            <div className="flex min-w-0 flex-col gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <ReviewStatusBadge status={currentProvenanceVersion?.reviewStatus} />
                <ProvenanceBadge
                  provenanceType={currentProvenanceVersion?.provenanceType}
                  generatedBy={currentProvenanceVersion?.generatedBy}
                />
                <span className="text-sm text-muted-foreground">
                  {t("operatorResponsibilityPanel.validFrom")}: {formatDate(currentProvenanceVersion?.validFrom)}
                </span>
              </div>
              <p className="min-w-0 break-words text-sm">{currentProvenanceVersion?.description}</p>
              <p className="min-w-0 break-words font-semibold">{provenance.provision?.citation}</p>
              <p className="min-w-0 break-words text-sm text-muted-foreground">
                {provenance.regulation?.title} · {provenance.regulation?.eliId} ·{" "}
                {t(
                  `operatorResponsibilityPanel.bindingForce.${provenance.regulation?.bindingForce || "unknown"}`,
                )}{" "}
                · {t("operatorResponsibilityPanel.asOf", { date: formatDate(provenance.regulation?.asOfDate) })}
              </p>
              <div className="max-h-[300px] min-w-0 overflow-y-auto whitespace-pre-wrap break-words rounded-md border p-3 text-sm">
                {provenance.provision?.text || t("operatorResponsibilityPanel.provisionTextUnavailable")}
              </div>
              <p className="text-xs text-warning">{t("operatorObligationsPanel.readOnlyNote")}</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="max-sm:w-full" onClick={() => setProvenance(null)}>
              {t("global.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Binding editor with mandatory effect preview */}
      <Dialog open={editorOpen} onOpenChange={(open) => { if (!open) setEditorOpen(false); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>{t("operatorObligationsPanel.createBinding")}</DialogTitle>
            <DialogDescription>{t("operatorObligationsPanel.bindingEditorSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex min-w-0 flex-wrap gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={anchorSelectId}>{t("operatorObligationsPanel.anchor")}</Label>
                <Select
                  value={anchorType}
                  onValueChange={(value: string) => {
                    setAnchorType(value as "Portfolio" | "Site");
                    setPreview(null);
                    setComparison(null);
                  }}
                >
                  <SelectTrigger id={anchorSelectId} className="w-[220px] max-w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Portfolio">{t("operatorObligationsPanel.portfolio")}</SelectItem>
                    <SelectItem value="Site">{t("operatorObligationsPanel.site")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {anchorType === "Site" ? (
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Label htmlFor={siteSelectId}>{t("operatorObligationsPanel.site")}</Label>
                  <Select
                    value={siteId || undefined}
                    onValueChange={(value: string) => {
                      setSiteId(value);
                      setPreview(null);
                      setComparison(null);
                    }}
                  >
                    <SelectTrigger id={siteSelectId} className="w-full min-w-[240px]">
                      <SelectValue placeholder={t("global.pleaseSelect")} />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)]">
                      {sites.map((site: any) => (
                        <SelectItem key={String(site._id)} value={String(site._id)} className="whitespace-normal">
                          {site.name || `${site.street} ${site.streetNumber}, ${site.city}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={allObjectsId}
                  checked={allObjects}
                  onCheckedChange={(checked) => {
                    setAllObjects(checked === true);
                    setPreview(null);
                    setComparison(null);
                  }}
                />
                <Label htmlFor={allObjectsId}>{t("operatorObligationsPanel.allRegulatedObjects")}</Label>
              </div>
              {!allObjects ? (
                <div className="flex max-h-[180px] min-w-0 flex-col gap-2 overflow-y-auto rounded-md border p-3">
                  {regulatedObjects.map((object: any) => {
                    const objectId = `${fieldId}-object-${object.code}`;
                    return (
                      <div key={object.code} className="flex items-start gap-2">
                        <Checkbox
                          id={objectId}
                          className="mt-0.5"
                          checked={selectedCodes.includes(object.code)}
                          onCheckedChange={(checked) => {
                            setSelectedCodes((codes) =>
                              checked === true ? [...codes, object.code] : codes.filter((code) => code !== object.code),
                            );
                            setPreview(null);
                            setComparison(null);
                          }}
                        />
                        <Label htmlFor={objectId} className="min-w-0 break-words font-normal">
                          {`${regulatedObjectName(t, object.code, object.name)}${
                            object.inPortfolio ? ` (${t("operatorObligationsPanel.inPortfolio")})` : ""
                          }`}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <div className="flex min-w-0 flex-wrap gap-4">
              <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
                <Label htmlFor={delegateNameId}>{t("operatorObligationsPanel.delegateName")}</Label>
                <Input
                  id={delegateNameId}
                  value={delegateName}
                  onChange={(event) => {
                    setDelegateName(event.target.value || "");
                    setPreview(null);
                    setComparison(null);
                  }}
                />
              </div>
              <div className="flex min-w-[240px] flex-1 flex-col gap-1.5">
                <Label htmlFor={delegateEmailId}>{t("operatorObligationsPanel.delegateEmail")}</Label>
                <Input
                  id={delegateEmailId}
                  type="email"
                  value={delegateEmail}
                  onChange={(event) => {
                    setDelegateEmail(event.target.value || "");
                    setPreview(null);
                    setComparison(null);
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.delegationNote")}</p>
            <div className="flex items-start gap-2">
              <Checkbox
                id="binding-include-unconfirmed"
                checked={includeUnconfirmed}
                onCheckedChange={(checked) => {
                  setIncludeUnconfirmed(checked === true);
                  setPreview(null);
                }}
              />
              <div className="flex min-w-0 flex-col gap-0.5">
                <Label htmlFor="binding-include-unconfirmed">{t("operatorObligationsPanel.includeUnconfirmed")}</Label>
                <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.includeUnconfirmedHint")}</p>
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor={classSelectId}>{t("operatorObligationsPanel.conformanceClass")}</Label>
              <Select
                value={conformanceClass}
                onValueChange={(value: string) => {
                  setConformanceClass(value);
                  setPreview(null);
                }}
              >
                <SelectTrigger id={classSelectId} className="w-[320px] max-w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  <SelectItem value="legalMinimum" className="whitespace-normal">
                    {t("operatorObligationsPanel.conformance.legalMinimum")}
                  </SelectItem>
                  <SelectItem value="recognizedRulesOfTechnology" className="whitespace-normal">
                    {t("operatorObligationsPanel.conformance.recognizedRulesOfTechnology")}
                  </SelectItem>
                  <SelectItem value="bestPractice" className="whitespace-normal">
                    {t("operatorObligationsPanel.conformance.bestPractice")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.conformanceNote")}</p>
            </div>
            {/* Conformance-class comparison: same scope priced under each class */}
            {comparison?.estimates ? (
              <div className="min-w-0 rounded-md border p-3">
                <p className="text-sm font-bold">{t("operatorObligationsPanel.classComparison")}</p>
                <div className="mt-2 min-w-0 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("operatorObligationsPanel.conformanceClass")}</TableHead>
                        <TableHead className="text-right">{t("operatorObligationsPanel.obligations")}</TableHead>
                        <TableHead className="text-right">{t("operatorObligationsPanel.inspectionsPerYear")}</TableHead>
                        <TableHead className="text-right">{t("operatorObligationsPanel.oneTimeCost")}</TableHead>
                        <TableHead className="text-right">{t("operatorObligationsPanel.annualCost")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparison.estimates.map((estimate: any) => (
                        <TableRow
                          key={estimate.conformanceClass}
                          className={
                            estimate.conformanceClass === conformanceClass ? "bg-accent" : undefined
                          }
                        >
                          <TableCell>
                            {/* Auswahl per Tastatur: die Zeile ist kein Bedienelement,
                                die Klassenbezeichnung ist es. */}
                            <button
                              type="button"
                              className="text-left underline underline-offset-2 hover:no-underline"
                              onClick={() => {
                                setConformanceClass(estimate.conformanceClass);
                                setPreview(null);
                              }}
                            >
                              {t(`operatorObligationsPanel.conformance.${estimate.conformanceClass}`)}
                            </button>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{estimate.obligationsMatched}</TableCell>
                          <TableCell className="text-right tabular-nums">{Math.round(estimate.inspectionsPerYear)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatEUR(estimate.oneTimeCost, comparison.currency)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatEUR(estimate.annualCost, comparison.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="mt-2 text-xs text-warning">
                  {t("operatorObligationsPanel.costEstimateDisclaimer")}
                </p>
              </div>
            ) : null}
            {preview ? (
              <Alert variant="info">
                <Info aria-hidden className="h-4 w-4" />
                <AlertTitle>{t("operatorObligationsPanel.previewTitle")}</AlertTitle>
                <AlertDescription>
                  <p className="min-w-0 break-words">
                    {/* With unreleased duties the count is not „bestätigt“ — it said so for every subscription. */}
                    {t(preview.includeUnconfirmed ? "operatorObligationsPanel.previewSummaryUnconfirmed" : "operatorObligationsPanel.previewSummary", {
                      assets: preview.assetsMatched,
                      sites: preview.sitesCovered,
                      obligations: preview.obligationsMatched,
                      oneTime: preview.oneTimeChecklistsNow,
                      plans: preview.periodicPlans,
                      faults: preview.faultTriggersArmed,
                    })}
                    {preview.delegatedTo
                      ? ` ${t("operatorObligationsPanel.previewDelegation", {
                          name: preview.delegatedTo.name || preview.delegatedTo.email,
                        })}`
                      : ""}
                  </p>
                  {preview.cost ? (
                    <p className="mt-2 font-semibold">
                      {t("operatorObligationsPanel.previewCost", {
                        oneTime: formatEUR(preview.cost.oneTimeCost, preview.cost.currency),
                        annual: formatEUR(preview.cost.annualCost, preview.cost.currency),
                      })}
                    </p>
                  ) : null}
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="max-sm:w-full" onClick={() => setEditorOpen(false)}>
              {t("global.cancel")}
            </Button>
            <Button
              variant="outline"
              className="max-sm:w-full"
              disabled={!scopeValid() || submitting}
              onClick={runPreview}
            >
              {submitting ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("operatorObligationsPanel.preview")}
            </Button>
            {/* Subscribing creates nothing — it needs no preview. Activating does, and waits for it. */}
            <Button
              variant="outline"
              className="max-sm:w-full"
              disabled={!scopeValid() || submitting}
              onClick={() => commitBinding(false)}
            >
              {t("operatorObligationsPanel.subscribe")}
            </Button>
            <Button className="max-sm:w-full" disabled={!preview || submitting} onClick={() => commitBinding(true)}>
              {submitting ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("operatorObligationsPanel.subscribeAndActivate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auswirkungen of one subscription */}
      <Dialog open={!!impactBinding} onOpenChange={(open) => { if (!open) setImpactBinding(null); }}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[1100px]">
          <DialogHeader>
            <DialogTitle>
              {t("operatorObligationsPanel.dashboard.title", { scope: impactBinding ? scopeSummary(impactBinding) : "" })}
            </DialogTitle>
          </DialogHeader>
          {impactBinding ? (
            <SubscriptionDashboardPanel sites={sites} refreshSignal={costRefresh} bindingId={String(impactBinding._id)} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delegation editor */}
      <Dialog
        open={!!delegationDialogBinding}
        onOpenChange={(open) => { if (!open) setDelegationDialogBinding(null); }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{t("operatorObligationsPanel.delegate")}</DialogTitle>
            <DialogDescription>{t("operatorObligationsPanel.delegationNote")}</DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 flex-wrap gap-4">
            <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
              <Label htmlFor={delegationNameId}>{t("operatorObligationsPanel.delegateName")}</Label>
              <Input
                id={delegationNameId}
                value={delegateName}
                onChange={(event) => setDelegateName(event.target.value || "")}
              />
            </div>
            <div className="flex min-w-[200px] flex-1 flex-col gap-1.5">
              <Label htmlFor={delegationEmailId}>{t("operatorObligationsPanel.delegateEmail")}</Label>
              <Input
                id={delegationEmailId}
                type="email"
                value={delegateEmail}
                onChange={(event) => setDelegateEmail(event.target.value || "")}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="max-sm:w-full"
              onClick={() => setDelegationDialogBinding(null)}
            >
              {t("global.cancel")}
            </Button>
            <Button
              className="max-sm:w-full"
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);

                try {
                  await legalAPI.setBindingDelegation(
                    String(delegationDialogBinding._id),
                    currentDelegation(),
                  );
                  setDelegationDialogBinding(null);
                  await load();
                } catch (error) {
                  pushError(error);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("global.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pflichten / Ausschlüsse — negative mask over this subscription's obligation set */}
      <Dialog open={!!exclusionsBinding} onOpenChange={(open) => { if (!open) setExclusionsBinding(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:max-w-[1280px]">
          <DialogHeader>
            <DialogTitle>{t("operatorObligationsPanel.exclusions.title")}</DialogTitle>
            <DialogDescription>{t("operatorObligationsPanel.exclusions.subtitle")}</DialogDescription>
          </DialogHeader>
          {exclusionsBinding ? (
            <SubscriptionExclusionsPanel binding={exclusionsBinding} canWrite={canWrite} onChanged={load} />
          ) : null}
          <DialogFooter>
            <Button variant="outline" className="max-sm:w-full" onClick={() => setExclusionsBinding(null)}>
              {t("global.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End binding — type-to-confirm, history is preserved */}
      <ConfirmationDialog
        open={!!endDialogBinding}
        setOpen={(open: boolean) => {
          if (!open) setEndDialogBinding(null);
        }}
        title={t("operatorObligationsPanel.endBindingTitle")}
        // Type the site (or „Portfolio“) to confirm — ending is final, so the name of what ends is asked.
        message={t("operatorObligationsPanel.endBindingMessage", {
          scope: endDialogBinding ? scopeSummary(endDialogBinding) : "",
          phrase: endDialogBinding ? anchorName(endDialogBinding) : "",
        })}
        checkString={endDialogBinding ? anchorName(endDialogBinding) : ""}
        okText={t("operatorObligationsPanel.end")}
        danger={true}
        onOK={async () => {
          try {
            await legalAPI.endBinding(String(endDialogBinding._id));
            setEndDialogBinding(null);
            await load();
          } catch (error) {
            pushError(error);
          }
        }}
      />
    </div>
  );
}
